/**
 * Database Table Node Component
 * Renders ERD-style database table with columns, data types, and constraints
 *
 * DUAL-MODE DESIGN:
 * - Export-safe foundation: Professional spacing, typography, colors for PNG/SVG export
 * - Interactive enhancements: Hover states, animations via @media (hover: hover)
 */

import { memo, useEffect, useState } from 'react'
import { NodeProps, NodeResizer, Handle, Position, useUpdateNodeInternals } from '@xyflow/react'
import { NodeConnectionHandles } from './NodeConnectionHandles'
import { columnHandleId } from '../../utils/erdHandles'
import { Copy, Check, KeyRound, Link2 } from 'lucide-react'
import type { TableContent } from '../../types/schema'

interface DatabaseTableNodeData {
  label: string
  tableContent?: TableContent
  style?: {
    // Colors
    headerBgColor?: string    // Header background
    headerTextColor?: string  // Header text
    bgColor?: string          // Body background
    borderColor?: string
    textColor?: string
    // Border
    borderWidth?: number
    borderStyle?: string
    // Opacity
    fillOpacity?: number
    textOpacity?: number
    // Text formatting
    fontSize?: number
    fontWeight?: string
    fontFamily?: string
    textAlign?: string
    verticalAlign?: string
    lineHeight?: number
    textDecoration?: string
    // Text behavior
    wordWrap?: boolean
    textOverflow?: string
    textPadding?: number
    letterSpacing?: number
  }
  minWidth?: number
  minHeight?: number
  maxWidth?: number
  maxHeight?: number
}

function DatabaseTableNode({ id, data, selected, width, height }: NodeProps) {
  const nodeData = (data as unknown) as DatabaseTableNodeData
  const tableContent = nodeData.tableContent
  const updateNodeInternals = useUpdateNodeInternals()

  // Force React Flow to measure the node after mount AND whenever the column set
  // changes — so the per-column FK handles below are (re)registered and edges
  // pinned to them never get silently dropped after an edit.
  const colKey = (tableContent?.columns ?? []).map((c) => c.name).join('|')
  useEffect(() => {
    updateNodeInternals(id)
  }, [id, colKey, updateNodeInternals])

  // Copy to clipboard state
  const [copiedItem, setCopiedItem] = useState<string | null>(null)

  const handleCopy = (text: string, itemId: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedItem(itemId)
      setTimeout(() => setCopiedItem(null), 2000)
    })
  }

  // Apply user-defined styles or defaults
  // Read from root level first (Properties Panel writes here), then fallback to nested style

  // Colors - Clean UI design with soft tinted header
  const headerBg = (nodeData as any).headerBgColor !== undefined ? (nodeData as any).headerBgColor : (nodeData.style?.headerBgColor || '#f8fafc')
  const headerTextColor = (nodeData as any).headerTextColor || nodeData.style?.headerTextColor || '#0f172a'
  const bodyBg = (nodeData as any).bgColor !== undefined ? (nodeData as any).bgColor : (nodeData.style?.bgColor || '#ffffff')
  const textColor = (nodeData as any).textColor || nodeData.style?.textColor || '#1f2937'
  const borderColor = (nodeData as any).borderColor !== undefined ? (nodeData as any).borderColor : (nodeData.style?.borderColor || '#cbd5e1')

  // Border - Subtle 1px for clean design
  const borderWidth = (nodeData as any).borderWidth !== undefined ? (nodeData as any).borderWidth : (nodeData.style?.borderWidth !== undefined ? nodeData.style.borderWidth : 1)
  const borderStyle = (nodeData as any).borderStyle || nodeData.style?.borderStyle || 'solid'

  // Opacity
  const fillOpacity = (nodeData as any).fillOpacity !== undefined ? (nodeData as any).fillOpacity : (nodeData.style?.fillOpacity !== undefined ? nodeData.style.fillOpacity : 1)
  const textOpacity = (nodeData as any).textOpacity !== undefined ? (nodeData as any).textOpacity : (nodeData.style?.textOpacity !== undefined ? nodeData.style.textOpacity : 1)

  // Text formatting
  const fontSize = (nodeData as any).fontSize !== undefined ? (nodeData as any).fontSize : (nodeData.style?.fontSize || 12)
  const fontWeight = (nodeData as any).fontWeight || nodeData.style?.fontWeight || '400'
  const fontFamily = (nodeData as any).fontFamily || nodeData.style?.fontFamily || 'JetBrains Mono, monospace'
  const textAlign = (nodeData as any).textAlign || nodeData.style?.textAlign || 'left'
  const textDecoration = (nodeData as any).textDecoration || nodeData.style?.textDecoration || 'none'

  // Text behavior
  const wordWrap = (nodeData as any).wordWrap !== undefined ? (nodeData as any).wordWrap : (nodeData.style?.wordWrap !== undefined ? nodeData.style.wordWrap : true)
  const textOverflow = (nodeData as any).textOverflow || nodeData.style?.textOverflow || 'ellipsis'
  const letterSpacing = (nodeData as any).letterSpacing !== undefined ? (nodeData as any).letterSpacing : (nodeData.style?.letterSpacing || 0)

  // Use measured width/height from React Flow, or defaults
  const nodeWidth = width || 280
  // Content-fit height: header + one row per column + optional index block.
  // (Was a fixed 300 → short tables sprawled with dead space.) A real resize wins.
  const ERD_ROW_H = 30
  const ERD_HEADER_H = 42
  const colCount = (tableContent as any)?.columns?.length ?? 0
  const idxCount = (tableContent as any)?.indexes?.length ?? 0
  const contentHeight = ERD_HEADER_H + colCount * ERD_ROW_H + (idxCount ? 26 + idxCount * 20 : 0) + 2
  const nodeHeight = (typeof height === 'number' && height > 60) ? height : contentHeight

  // Per-column connection points (for column-anchored crow's-foot FK edges).
  // Vertical center of each row is deterministic because rows are fixed-height.
  const colHandles = (tableContent?.columns ?? []).map((c, i) => ({
    name: c.name,
    top: ERD_HEADER_H + i * ERD_ROW_H + ERD_ROW_H / 2,
  }))

  // Fallback for basic node without tableContent
  if (!tableContent) {
    return (
      <div
        className="bg-white border-2 border-gray-300 rounded shadow-md flex flex-col"
        style={{ width: nodeWidth, height: nodeHeight, minWidth: 200, minHeight: 100 }}
      >
        <div className="bg-gray-100 px-3 py-2 border-b border-gray-300 font-semibold">
          {nodeData.label}
        </div>
        <div className="px-3 py-2 text-sm text-gray-500 italic">
          No table content defined
        </div>
        <NodeConnectionHandles />
      </div>
    )
  }

  return (
    <>
      {/* Interactive Enhancements - Only apply when hover is supported (not during export) */}
      <style>
        {`
          /* Interactive polish - automatically stripped during PNG/SVG export */
          @media (hover: hover) {
            .table-row:hover {
              background-color: #f9fafb !important;
              transition: background-color 150ms ease;
            }

            /* Copy button: hidden by default, fades in on row hover */
            .table-row .copy-button {
              opacity: 0 !important;
              transition: opacity 150ms ease;
            }

            .table-row:hover .copy-button {
              opacity: 0.6 !important;
            }

            .copy-button:hover {
              opacity: 1 !important;
            }

            .copy-button:focus-visible {
              outline: 2px solid #3b82f6;
              outline-offset: 2px;
              border-radius: 3px;
            }
          }
        `}
      </style>

      {selected && (
        <NodeResizer
          minWidth={200}
          minHeight={100}
        />
      )}

      <div
        className="rounded shadow-md flex flex-col"
        style={{
          width: nodeWidth,
          height: nodeHeight,
          borderWidth: borderWidth,
          borderStyle: borderStyle,
          borderColor: borderColor,
          backgroundColor: bodyBg,
          opacity: fillOpacity,
          overflow: 'hidden',
          fontFamily: fontFamily,
          color: textColor
        }}
      >
        {/* Table Header - Fixed height, export-optimized spacing */}
        <div
          className="flex-shrink-0 flex items-center justify-between gap-2"
          style={{
            backgroundColor: headerBg,
            color: headerTextColor,
            fontSize: fontSize + 2,
            fontWeight: fontWeight,
            fontFamily: fontFamily,
            padding: '10px 14px',  // Tighter header
            textDecoration: textDecoration,
            letterSpacing: `${letterSpacing}px`,
            opacity: textOpacity
          }}
        >
          <span
            style={{
              overflow: textOverflow === 'visible' ? 'visible' : 'hidden',
              textOverflow: textOverflow === 'visible' ? undefined : (textOverflow as any),
              whiteSpace: textOverflow === 'ellipsis' ? 'nowrap' : (wordWrap ? 'normal' : 'nowrap'),
              flex: 1,
              textAlign: textAlign as any
            }}
            title={tableContent.tableName}
          >
            {tableContent.tableName}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleCopy(tableContent.tableName, 'table-name')
            }}
            className="flex-shrink-0 hover:opacity-70 transition-opacity"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: headerTextColor, padding: '2px' }}
            title="Copy table name"
          >
            {copiedItem === 'table-name' ? (
              <Check size={14} />
            ) : (
              <Copy size={14} />
            )}
          </button>
        </div>

        {/* Columns List - Scrollable */}
        <div
          className="flex-1 overflow-y-auto"
          style={{
            minHeight: 0,
            overflowX: textOverflow === 'visible' ? 'visible' : 'hidden'
          }}
        >
          {tableContent.columns.map((column, index) => (
            <div
              key={index}
              className="table-row"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'nowrap',
                height: ERD_ROW_H,
                padding: '0 12px',
                borderBottom: '1px solid #f1f5f9',
                fontSize: 13,
                fontFamily,
                minWidth: 0,
              }}
            >
              {/* Key glyph: PK (gold key) / FK (blue link), fixed slot */}
              <span style={{ width: 16, flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {column.isPrimaryKey
                  ? <KeyRound size={13} color="#d97706" />
                  : column.isForeignKey
                    ? <Link2 size={13} color="#2563eb" />
                    : null}
              </span>

              {/* Column name (left, bold, truncates) */}
              <span
                style={{ fontWeight: 600, color: textColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flexShrink: 1 }}
                title={column.name}
              >
                {column.name}
              </span>

              {/* Type (pushed right, muted monospace) */}
              <span
                style={{ marginLeft: 'auto', flexShrink: 0, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', paddingLeft: 8 }}
                title={column.dataType + (column.defaultValue ? ` = ${column.defaultValue}` : '')}
              >
                {column.dataType}
              </span>

              {/* Constraint chips (UQ / NN — PK/FK already shown as the key glyph) */}
              {column.isUnique && (
                <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, padding: '1px 4px', borderRadius: 3, background: '#f3e8ff', color: '#6b21a8' }}>UQ</span>
              )}
              {column.isNotNull && (
                <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, padding: '1px 4px', borderRadius: 3, background: '#f1f5f9', color: '#475569' }}>NN</span>
              )}
            </div>
          ))}
        </div>

        {/* Indexes Section (Optional) */}
        {tableContent.indexes && tableContent.indexes.length > 0 && (
          <div className="border-t-2 border-gray-300 bg-gray-50 flex-shrink-0">
            <div className="px-3 py-1 text-xs font-semibold text-gray-600">
              Indexes ({tableContent.indexes.length})
            </div>
            {tableContent.indexes.map((index, idx) => (
              <div
                key={idx}
                className="px-3 py-1 text-xs text-gray-600"
                style={{
                  overflow: textOverflow === 'visible' ? 'visible' : 'hidden',
                  textOverflow: textOverflow === 'visible' ? undefined : ('ellipsis'),
                  whiteSpace: 'nowrap'
                }}
                title={`${index.name}: ${index.columns.join(', ')}`}
              >
                📇 {index.name}: {index.columns.join(', ')}
              </div>
            ))}
          </div>
        )}

        <NodeConnectionHandles />

        {/* Per-column connection points for column-anchored FK crow's-foot edges.
            Each column exposes a source AND target handle on BOTH sides (ids from
            the shared columnHandleId helper) so a derived edge can attach from
            either direction regardless of table layout. Visually unobtrusive —
            these exist for programmatic FK edges, not user drag. */}
        {colHandles.map((c) =>
          (['left', 'right'] as const).map((side) => {
            const hid = columnHandleId(c.name, side)
            const pos = side === 'left' ? Position.Left : Position.Right
            const style = {
              top: c.top,
              width: 7,
              height: 7,
              minWidth: 0,
              minHeight: 0,
              background: '#cbd5e1',
              border: 'none',
              opacity: 0.01,
            } as const
            return (
              <span key={hid}>
                <Handle type="source" id={hid} position={pos} style={style} isConnectable={false} />
                <Handle type="target" id={hid} position={pos} style={style} isConnectable={false} />
              </span>
            )
          })
        )}
      </div>
    </>
  )
}

export default memo(DatabaseTableNode)
